import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import WarningIcon from '@mui/icons-material/Warning';
import { Accordion, Dialog } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import OnFileDownloadIcon from '../../../icons/OnFileDownloadIcon';
import {
    createAppointment,
    loadMostRecentAppointmentAttendanceDetails,
    searchAppointmentsAndSlots,
    updateAppointmentAttendanceDetail
} from '../../../store/calendarService';
import { clearParticipantsCourses } from '../../../store/courseService';
import { selectAttendedStatusId } from '../../../store/dataSelectors';
import {
    loadActionPlanStatusDetails,
    loadAppointmentTypeDetails,
    loadAssetRequestAssetDetails,
    loadAssetRequestStatusDetails,
    loadAttendanceDetails,
    loadBarrierNameDetails,
    loadContractDetailsForContractId,
    loadDocumentTypes,
    loadFinancialRequestStatusDetails,
    loadFinancialRequestTypeDetails,
    loadInCludedFieldsInInitialAppointment,
    loadJobSectorDetails,
    loadLocationIdsAndNames,
    loadServiceDetailsForContractId,
    loadVacancyStageDetails
} from '../../../store/directusService';
import {
    downloadParticipantDocumentById,
    searchDocumentDetails
} from '../../../store/documentService';
import {
    clearParticipantActionPlans,
    clearParticipantAssetRequests,
    clearParticipantBarriers,
    clearParticipantFinancialRequests,
    clearParticipantJobGoals,
    clearQuestionnaireAnswers,
    loadAppointmentSectionHeadings,
    loadContractConstants,
    loadQuestionnaireAnswersByParticipant,
    loadQuestionnaires
} from '../../../store/participantService';
import { clearVacancySubmissions } from '../../../store/recruitmentService';
import { selectLoggedInUserFirstAndLastName } from '../../../store/userSelectors';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { clearKeys, trimEntries } from '../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import AllActiveCourseAndProvisionManagement from '../../courses/allActiveCourseAndProvision/AllActiveCourseAndProvisionManagement';
import Button from '../../formElements/Button';
import CancelButton from '../../formElements/CancelButton';
import SingleSelect from '../../formElements/SingleSelect';
import ParticipantStickyMenu from '../../navigation/participant/ParticipantStickyMenu';
import ActionPlan from '../../participant/actionPlan/ActionPlan';
import Barriers from '../../participant/barriers/Barriers';
import JobGoals from '../../participant/jobGoals/JobGoals';
import Questionnaire from '../../participant/questionnaire/Questionnaire';
import Vacancies from '../../participant/submissions/vacancies/Vacancies';
import SupportedJobSearchManagement from '../../participant/supportedJobSearch/SupportedJobSearchManagement';
import MandatoryActivitiesSwitch from '../../scheduling/components/MandatoryActivitiesSwitch';
import AccordionHeader from '../../ui/accordion/AccordionHeader';
import AccordionIcon from '../../ui/accordion/AccordionIcon';
import ScreenError from '../../ui/messageBanners/ScreenError';
import ConfirmPrompt from '../../ui/notices/confirmPrompt/ConfirmPrompt';

import AppointmentPdfManagement from './pdfManagement/AppointmentPDFManagement';
import {
    addWarning,
    clearWarning,
    validateAttendance,
    validateAttendanceSelect,
    validateUpdateAttendanceStatus
} from './validation/validateAppointments';
import ESignature from './ESignature';
import General from './General';
import InitialAppointment from './InitialAppointment';
import NotesCurrent from './NotesCurrent';
import NotesPast from './NotesPast';
import Requests from './Requests';

import app from '../../../app.module.css';
import local from './styles/appointments.module.css';

const _ATTENDANCE_UPDATE_MESSAGE = 'Appointment attendance status has been updated';
const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const updateRoles = [QUALITY, SUPERUSER];
const uniqueIdEditRoles = [LOCAL_ADMIN, QUALITY, SUPERUSER];
const downloadRoles = [
    ADVISER,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];

const accordions = [
    'questionnaire',
    'jobReadiness',
    'barriers',
    'actionPlan',
    'courses',
    'request',
    'supportedJobSearch',
    'vacancy'
];

const pdfRequiredData = [
    'questionnaire',
    'jobReadiness',
    'barriers',
    'actionPlan',
    'courses',
    'request',
    'vacancy'
];

const Appointments = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialIsActiveState = {
        questionnaire: false,
        jobReadiness: false,
        barriers: false,
        actionPlan: false,
        courses: false,
        supportedJobSearch: false,
        request: false,
        vacancy: false
    };

    const initialCurrentAppointment = {
        id: '',
        attendanceStatusId: '',
        participantId: '',
        attendanceName: '',
        lastAppointmentNotes: '',
        currentNotes: '',
        nextAppointmentNotes: '',
        calendarAppointmentId: '',
        endTime: '',
        startTime: '',
        mostRecentAppointmentId: ''
    };

    const initialNextAppointment = {
        date: ' - ',
        startTime: ' - ',
        appointmentType: ' - '
    };

    const initialRequired = {
        poNumber: false,
        bookNextAppointment: false,
        diagnosticQuestionnaire: false,
        gender: false,
        dateOfBirth: false
    };

    const [isInitiallyActive, setIsInitiallyActive] = useState(initialIsActiveState);
    const [isActive, setIsActive] = useState(initialIsActiveState);
    const [participantsActiveAccordions, setParticipantsActiveAccordions] = useState({});

    const [showInitialAppointmentAccordion, setShowInitialAppointmentAccordion] = useState(false);
    const [isRequired, setIsRequired] = useState(initialRequired);
    const [hasRequired, setHasRequired] = useState(initialRequired);
    const [initialStateEntry, setInitialStateEntry] = useState({});
    const [initialAppointment, setInitialAppointment] = useState({}); // 'Initial' type of appointment.
    const [initialAppointmentWarnings, setInitialAppointmentWarnings] = useState({});

    const [initialAttendanceStatusId, setInitialAttendanceStatusId] = useState('');
    const [attendanceName, setAttendanceName] = useState('');
    const [currentAppointment, setCurrentAppointment] = useState(initialCurrentAppointment);
    const [nextAppointment, setNextAppointment] = useState(initialNextAppointment);
    const [exitFormTemplateId, setExitFormTemplateId] = useState('');

    const [complianceActivityFormQuestionnaireId, setComplianceActivityFormQuestionnaireId] =
        useState('');
    const [doubtFormId, setDoubtFormId] = useState('');
    const [isProceedButtonDisabled, setIsProceedButtonDisabled] = useState(true);

    const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [generatePDF, setGeneratePDF] = useState(false);
    const [adviserSignature, setAdviserSignature] = useState('');
    const [participantSignature, setParticipantSignature] = useState('');
    const [signatureDateTime, setSignatureDateTime] = useState('');
    const [fileName, setFileName] = useState('');

    const [expanded, setExpanded] = useState('');
    const [errors, setErrors] = useState({});
    const [warnings, setWarnings] = useState({});
    const [keys, setKeys] = useState({ selectedAttendanceStatus: '0' });

    const [isViewOnly, setIsViewOnly] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
    const [isAccordionDisabled, setIsAccordionDisabled] = useState(true);
    const [isUniqueIdEditDisabled, setIsUniqueIdEditDisabled] = useState(true);
    const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const sectionHeadings = useSelector(
        (state) => state.entities.participantService.appointmentSectionHeadings
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const appointmentTypes = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const mostRecentAppointment = useSelector(
        (state) => state.entities.calendarService.mostRecentAppointment
    );
    const appointments = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsSearch
    );
    const appointmentsAndSlotsMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsSearchMetaData
    );
    const loadingAppointmentsAndSlots = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentsAndSlotsSearch
    );
    const includedFieldsInInitialAppointment = useSelector(
        (state) => state.entities.directusService.includedFieldsInInitialAppointment
    );
    const questionnaires = useSelector((state) => state.entities.participantService.questionnaires);
    const questionnaireAnswers = useSelector(
        (state) => state.entities.participantService.questionnaireAnswers
    );
    const loadingQuestionnaireAnswers = useSelector(
        (state) => state.entities.participantService.loadingQuestionnaireAnswers
    );
    const loadingQuestionnaires = useSelector(
        (state) => state.entities.participantService.loadingQuestionnaires
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const barrierNameDetails = useSelector(
        (state) => state.entities.directusService.barrierNameDetails
    );
    // for action plan
    const actionPlanStatuses = useSelector(
        (state) => state.entities.directusService.actionPlanStatusDetails
    );

    // for courses
    const locationIdsAndNames = useSelector(
        (state) => state.entities.directusService.locationIdsAndNames
    );
    // for financial requests
    const financialRequestTypeDetails = useSelector(
        (state) => state.entities.directusService.financialRequestTypeDetails
    );
    const financialRequestStatusDetails = useSelector(
        (state) => state.entities.directusService.financialRequestStatusDetails
    );
    // for asset requests
    const assetRequestStatusDetails = useSelector(
        (state) => state.entities.directusService.assetRequestStatusDetails
    );
    const assetRequestAssetDetails = useSelector(
        (state) => state.entities.directusService.assetRequestAssetDetails
    );
    // for vacancies
    const vacancyStageDetails = useSelector(
        (state) => state.entities.directusService.vacancyStageDetails
    );
    const documentDetailsSearch = useSelector(
        (state) => state.entities.documentService.documentDetailsSearch
    );
    const attendedStatusId = useSelector(selectAttendedStatusId);
    const adviserName = useSelector(selectLoggedInUserFirstAndLastName);

    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (includedFieldsInInitialAppointment?.length < 1)
            dispatch(loadInCludedFieldsInInitialAppointment());
        if (attendanceDetails?.length < 1) dispatch(loadAttendanceDetails());
        if (documentTypes?.length < 1) dispatch(loadDocumentTypes());
        if (appointmentTypes?.length < 1) dispatch(loadAppointmentTypeDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!state?.action || !roles) return;
        const viewOnly = state.action === 'view';
        setIsViewOnly(viewOnly);
        setIsDisabled(!hasRole(acceptedRoles, roles) || viewOnly);
        setIsUpdateDisabled(!hasRole(updateRoles, roles) || viewOnly);
        setIsAccordionDisabled(!hasRole(acceptedRoles, roles));
        setIsUniqueIdEditDisabled(!hasRole(uniqueIdEditRoles, roles) || viewOnly);

        if (state?.showWarning) {
            setIsRequired((prev) => ({ ...prev, poNumber: true }));
            setWarnings(addWarning(warnings, 'poNumber'));
        }
        if (state?.panel) setExpanded(state.panel);
    }, [roles, state]);

    useEffect(() => {
        if (!currentParticipant?.id || currentParticipant.id !== state?.participantId) return;
        dispatch(loadAppointmentSectionHeadings(currentParticipant.contractId));
        dispatch(loadContractConstants(currentParticipant.contractId));
        dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
        dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        dispatch(loadMostRecentAppointmentAttendanceDetails(currentParticipant.id));
        dispatch(
            searchAppointmentsAndSlots(
                {
                    participantIds: [currentParticipant.id]
                },
                0
            )
        );

        if (state?.appointment?.type?.toLowerCase() === 'initial') {
            let hasRequiredUpdates = { ...hasRequired };
            let newWarnings = { ...initialAppointmentWarnings };

            const updateWarning = (key) => {
                if (isRequired[key] && !hasRequired[key])
                    hasRequiredUpdates = { ...hasRequiredUpdates, [key]: true };
                if (initialAppointmentWarnings?.[key]) newWarnings = clearWarning(newWarnings, key);
            };
            if (currentParticipant.dateOfBirth) updateWarning('dateOfBirth');
            if (currentParticipant.genderId) updateWarning('gender');

            setHasRequired(hasRequiredUpdates);
            setInitialAppointmentWarnings(newWarnings);
        }

        const participantsAppointment = state?.appointment?.attendances?.find(
            (el) => el.participantId === currentParticipant.id
        );
        if (!participantsAppointment?.id) return;
        const isNewAppointment = participantsAppointment.status?.toLowerCase() === 'not known';
        setIsDownloadDisabled(isNewAppointment || !hasRole(downloadRoles, roles));
        if (!isNewAppointment) {
            dispatch(
                searchDocumentDetails(
                    {
                        participantId: currentParticipant.id,
                        attendanceIds: [participantsAppointment.id]
                    },
                    0
                )
            );
        }
    }, [currentParticipant, state]);

    useEffect(() => {
        if (sectionHeadings?.contractId === currentParticipant?.contractId) {
            const accordionStatus = accordions.reduce(
                (acc, cur) => ({ ...acc, [cur]: sectionHeadings[cur] }),
                {}
            );
            setIsActive(accordionStatus);
            setIsInitiallyActive(accordionStatus);
            const keysNotForPdf = accordions.filter((el) => !pdfRequiredData.includes(el));
            const dataForPdf = trimEntries(keysNotForPdf, accordionStatus);
            setParticipantsActiveAccordions(dataForPdf);

            if (accordionStatus.courses) {
                locationIdsAndNames?.length < 1 && dispatch(loadLocationIdsAndNames());
            }
            if (accordionStatus.request) {
                assetRequestAssetDetails?.length < 1 && dispatch(loadAssetRequestAssetDetails());
                assetRequestStatusDetails?.length < 1 && dispatch(loadAssetRequestStatusDetails());
                financialRequestStatusDetails?.length < 1 &&
                    dispatch(loadFinancialRequestStatusDetails());
                financialRequestTypeDetails?.length < 1 &&
                    dispatch(loadFinancialRequestTypeDetails());
            }
            if (accordionStatus.actionPlan || accordionStatus.barriers) {
                barrierNameDetails?.length < 1 && dispatch(loadBarrierNameDetails());
                if (accordionStatus.actionPlan && actionPlanStatuses?.length < 1)
                    dispatch(loadActionPlanStatusDetails());
            }
            if (accordionStatus.jobReadiness || accordionStatus.vacancy) {
                jobSectorDetails?.length < 1 && dispatch(loadJobSectorDetails());
                if (accordionStatus.vacancy && vacancyStageDetails?.length < 1)
                    dispatch(loadVacancyStageDetails());
            }
        }
    }, [sectionHeadings]);

    useEffect(() => {
        if (
            Object.keys(contractConstants).length < 1 ||
            state?.appointment?.type?.toLowerCase() !== 'initial' ||
            includedFieldsInInitialAppointment?.length < 1 ||
            Object.values(initialAppointmentWarnings).length > 0
        )
            return;
        if (contractConstants.displayInitialAppointmentAccordion)
            setShowInitialAppointmentAccordion(true);
        if (!contractConstants.initialAppointmentFields?.length) return;
        let newWarnings = { ...initialAppointmentWarnings };
        const bookNextAppointmentId = includedFieldsInInitialAppointment.find(
            (el) => el.name.toLowerCase() === 'book next appointment'
        )?.id;
        if (
            bookNextAppointmentId &&
            contractConstants.initialAppointmentFields.includes(bookNextAppointmentId)
        ) {
            setIsRequired((prev) => ({ ...prev, bookNextAppointment: true }));
            newWarnings = addWarning(newWarnings, 'bookNextAppointment');
        }

        const diagnosticQuestionnaireId = includedFieldsInInitialAppointment.find(
            (el) => el.name.toLowerCase() === 'diagnostic questionnaire'
        )?.id;
        if (
            diagnosticQuestionnaireId &&
            contractConstants.initialAppointmentFields.includes(diagnosticQuestionnaireId)
        ) {
            setIsRequired((prev) => ({ ...prev, diagnosticQuestionnaire: true }));
            dispatch(loadQuestionnaires());
            dispatch(loadQuestionnaireAnswersByParticipant(currentParticipant.id));
        }

        const genderId = includedFieldsInInitialAppointment.find(
            (el) => el.name.toLowerCase() === 'gender'
        )?.id;
        if (genderId && contractConstants.initialAppointmentFields.includes(genderId)) {
            setIsRequired((prev) => ({ ...prev, gender: true }));
            if (currentParticipant.genderId) setHasRequired((prev) => ({ ...prev, gender: true }));
            else newWarnings = addWarning(newWarnings, 'gender');
        }

        const dateOfBirthId = includedFieldsInInitialAppointment.find(
            (el) => el.name.toLowerCase() === 'date of birth'
        )?.id;
        if (dateOfBirthId && contractConstants.initialAppointmentFields.includes(dateOfBirthId)) {
            setIsRequired((prev) => ({ ...prev, dateOfBirth: true }));
            if (currentParticipant.dateOfBirth)
                setHasRequired((prev) => ({ ...prev, dateOfBirth: true }));
            else newWarnings = addWarning(newWarnings, 'dateOfBirth');
        }
        setInitialAppointmentWarnings(newWarnings);
    }, [state, includedFieldsInInitialAppointment, contractConstants]);

    useEffect(() => {
        if (!isRequired.diagnosticQuestionnaire) return;
        if (loadingQuestionnaires || loadingQuestionnaireAnswers) return;
        let add = false;
        if (questionnaires?.length) {
            const diagnosticQuestionnaireIds = questionnaires
                .filter(
                    (el) =>
                        el.type?.toLowerCase() === 'diagnostic' ||
                        el.title?.toLowerCase().includes('diagnostic questionnaire')
                )
                .map((el) => el.id);
            if (diagnosticQuestionnaireIds.length < 1) add = true;
            else {
                const questionnaire = questionnaireAnswers.find((el) =>
                    diagnosticQuestionnaireIds.includes(el.questionnaireId)
                );
                if (
                    !questionnaire ||
                    questionnaire.participantId !== currentParticipant.id ||
                    !questionnaire.dateStarted
                )
                    add = true;
            }
        } else add = true;

        add
            ? addInitialAppointmentWarning('diagnosticQuestionnaire')
            : removeInitialAppointmentWarning('diagnosticQuestionnaire');
    }, [
        questionnaireAnswers,
        questionnaires,
        loadingQuestionnaires,
        loadingQuestionnaireAnswers,
        isRequired
    ]);

    useEffect(() => {
        const isInitialAppointmentWarnings = Object.values(initialAppointmentWarnings).length > 0;
        if (isInitialAppointmentWarnings) setWarnings(addWarning(warnings, 'initialAppointment'));
        if (warnings?.initialAppointment) {
            if (!isInitialAppointmentWarnings)
                setWarnings(clearWarning(warnings, 'initialAppointment'));
        }
    }, [initialAppointmentWarnings]);

    useEffect(() => {
        if (
            !loadingAppointmentsAndSlots &&
            appointments.length > 0 &&
            'number' in appointmentsAndSlotsMetaData &&
            !appointmentsAndSlotsMetaData.last
        ) {
            dispatch(
                searchAppointmentsAndSlots(
                    {
                        participantIds: [currentParticipant.id]
                    },
                    appointmentsAndSlotsMetaData.number + 1
                )
            );
        }
    }, [appointments, loadingAppointmentsAndSlots, appointmentsAndSlotsMetaData]);

    useEffect(() => {
        const attendanceTitle =
            attendanceDetails.find((el) => el.id === currentAppointment.attendanceStatusId)?.name ||
            '';
        setAttendanceName(attendanceTitle);
        if (attendanceTitle === 'Failed to Attend' || attendanceTitle.includes('Cancelled')) {
            setIsActive((prev) => ({
                ...prev,
                questionnaire: false,
                jobReadiness: false,
                barriers: false,
                actionPlan: false,
                courses: false,
                supportedJobSearch: false,
                request: false,
                vacancy: false
            }));
        } else setIsActive(isInitiallyActive);
    }, [currentAppointment.attendanceStatusId]);

    useEffect(() => {
        if (attendanceDetails?.length < 1) return;
        setCurrentAppointment((prev) => ({
            ...prev,
            participantId: currentParticipant?.id,
            calendarAppointmentId: state?.appointment?.id,
            lastAppointmentNotes: mostRecentAppointment.nextAppointmentNotes || '',
            mostRecentAppointmentId: mostRecentAppointment.id
        }));
        initializeAttendance();
    }, [attendanceDetails, mostRecentAppointment]);

    useEffect(() => {
        if (state.action === 'start') {
            setIsProceedButtonDisabled(
                Object.values(errors).some((el) => el.error) || attendanceName === 'Not known'
            );
        }
    }, [errors, attendanceName]);

    useEffect(() => {
        if (successMessage === _ATTENDANCE_UPDATE_MESSAGE) navigate(state?.returnPath);
    }, [successMessage]);

    // HELPER FNS
    const configNextAppointment = () => {
        const appointmentList = Object.keys(initialAppointment).length
            ? [initialAppointment, ...appointments]
            : appointments;
        const baseTimestamp = new Date(
            `${state?.appointment?.date} ${state?.appointment?.startTime}`
        ).getTime();
        let closestFutureDate = null;
        let appointment = null;
        let appointmentType = null;
        appointmentList.forEach((el) => {
            const timestamp = new Date(`${el.date} ${el.startTime}`).getTime();
            if (
                timestamp > baseTimestamp &&
                (closestFutureDate === null || timestamp < closestFutureDate)
            ) {
                closestFutureDate = timestamp;
                appointment = el;
                appointmentType = el.type;
            }
        });
        setNextAppointment({
            date: reverseFormatDate(appointment?.date) || ' - ',
            startTime: appointment?.startTime || ' - ',
            appointmentType: appointmentType || ' - '
        });
    };

    const addInitialAppointmentWarning = (key) => {
        if (isRequired[key]) {
            if (hasRequired[key]) {
                setHasRequired((prev) => ({ ...prev, [key]: false }));
            }
            if (!(key in initialAppointmentWarnings)) {
                setInitialAppointmentWarnings(addWarning(initialAppointmentWarnings, key));
            }
        }
    };

    const removeInitialAppointmentWarning = (key) => {
        if (isRequired[key]) {
            if (!hasRequired[key]) setHasRequired((prev) => ({ ...prev, [key]: true }));
            if (initialAppointmentWarnings?.[key]) {
                setInitialAppointmentWarnings(clearWarning(initialAppointmentWarnings, key));
            }
        }
    };

    const initializeAttendance = () => {
        const entry = state.appointment.attendances.find(
            (el) => el.participantId === currentParticipant.id
        );
        if (entry) {
            setCurrentAppointment((prev) => ({
                ...prev,
                attendanceStatusId: entry?.statusId,
                attendanceName: entry?.name,
                attendanceTime: format(new Date(), `dd / MM / yyyy , HH :mm`).replace(/:/g, ' : '),
                currentNotes: entry?.notes,
                nextAppointmentNotes: entry?.nextAppointmentNotes
            }));
            setInitialStateEntry(entry);
        }
        setInitialAttendanceStatusId(entry?.statusId);
    };

    const configDocumentName = () => {
        const fileName = `${state?.appointment?.type.replaceAll(' ', '_')}_${format(
            new Date(),
            `dd_MMM_yyyy_HH_mm`
        )}`;
        setFileName(fileName);
    };

    const resetAppointment = () => {
        setCurrentAppointment(initialCurrentAppointment);
        initializeAttendance();
        setKeys(clearKeys(keys));
        setExpanded('');
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((errors) => ({ ...errors, [key]: { error: false, message: '' } }));
    };

    const cleanUp = () => {
        dispatch(clearParticipantBarriers());
        dispatch(clearQuestionnaireAnswers());
        dispatch(clearParticipantActionPlans());
        dispatch(clearParticipantAssetRequests());
        dispatch(clearParticipantFinancialRequests());
        dispatch(clearParticipantsCourses());
        dispatch(clearParticipantJobGoals());
        dispatch(clearVacancySubmissions());
        setErrors({});
        setWarnings({});
        setInitialAppointmentWarnings({});
        setInitialAppointment({});
        setInitialAttendanceStatusId('');
        setIsRequired(initialRequired);
        setHasRequired(initialRequired);
    };

    // EVENT HANDLERS
    const onUpdateNotes = (key, value) => {
        setCurrentAppointment((prev) => ({ ...prev, [key]: value }));
    };

    const onAttendanceSelect = (id) => {
        setErrors({});
        setExpanded('');
        const newErrors = validateAttendanceSelect(
            state,
            id,
            attendedStatusId,
            isRequired,
            hasRequired
        );
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
        }
        setCurrentAppointment((prev) => ({
            ...prev,
            attendanceStatusId: id,
            attendanceName: attendanceDetails.find((el) => el.id === id)?.name
        }));
    };

    const onAccordionChange = (panel) => {
        setExpanded(expanded === panel ? '' : panel);
        if (currentAppointment.attendanceStatusId !== initialAttendanceStatusId) {
            setCurrentAppointment((prev) => ({
                ...prev,
                attendanceStatusId: initialAttendanceStatusId,
                attendanceName: ''
            }));
        }
        Object.keys(errors).length && setErrors({});
    };

    const onCancelAppointment = () => {
        if (state.action === 'view') navigate(state.returnPath);
        else setIsConfirmModalOpen(true);
    };

    const onCompleteAppointment = () => {
        const payload = {
            statusId: currentAppointment.attendanceStatusId,
            notes: currentAppointment.currentNotes,
            nextAppointmentNotes: currentAppointment.nextAppointmentNotes
        };
        dispatch(updateAppointmentAttendanceDetail(payload, state.appointmentAttendanceId));
    };

    const onUpdateAppointmentEntryAttendanceStatus = () => {
        const newErrors = validateUpdateAttendanceStatus(
            currentAppointment.attendanceStatusId,
            initialAttendanceStatusId
        );
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            statusId: currentAppointment.attendanceStatusId,
            notes: currentAppointment.currentNotes,
            nextAppointmentNotes: currentAppointment.nextAppointmentNotes
        };
        dispatch(
            updateAppointmentAttendanceDetail(
                payload,
                state.appointmentAttendanceId,
                _ATTENDANCE_UPDATE_MESSAGE
            )
        );
    };

    const onUpdateInitialAppointment = (data) => {
        setInitialAppointment(data);
        const isAppointment = Object.keys(data).length > 0;
        isAppointment
            ? removeInitialAppointmentWarning('bookNextAppointment')
            : addInitialAppointmentWarning('bookNextAppointment');
    };

    const onProceed = () => {
        clearError('appointmentsProceedButton');
        const newErrors = validateAttendance(currentAppointment);
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }
        contractConstants?.displayESignature ? setIsSignatureModalOpen(true) : onSignatureSkip();
        setExitFormTemplateId(contractConstants?.exitFormTemplateId || '');
        setComplianceActivityFormQuestionnaireId(
            contractConstants?.complianceActivityFormQuestionnaireId || ''
        );
        setDoubtFormId(contractConstants?.doubtFormIds[0] || '');
    };

    const onSavePDF = () => {
        if (isRequired.bookNextAppointment && hasRequired.bookNextAppointment) {
            const { type, ...rest } = initialAppointment;
            dispatch(createAppointment(type, rest));
        }
        resetAppointment();
    };

    const onAdviserSignatureSave = (signature) => setAdviserSignature(signature);

    const onParticipantSignatureSave = (signature) => {
        setParticipantSignature(signature);
        setSignatureDateTime(format(new Date(), `dd  MMM,  yyyy   HH:mm`));
        configDocumentName();
    };

    const onSignatureConfirm = () => {
        setIsSignatureModalOpen(false);
        setGeneratePDF(true);
        onCompleteAppointment();
        configNextAppointment();
    };

    const onSignatureSkip = () => {
        setIsSignatureModalOpen(false);
        setGeneratePDF(true);
        onCompleteAppointment();
        configNextAppointment();
        setSignatureDateTime(format(new Date(), `dd MMM, yyyy HH:mm`));
        configDocumentName();
    };

    const onSignatureCancel = () => setIsSignatureModalOpen(false);

    const onSignatureModalClose = (event, reason) => {
        if (reason !== 'backdropClick') {
            setIsSignatureModalOpen(false);
        }
    };

    const onConfirmModalClose = (_, cancelChanges) => {
        if (cancelChanges) {
            resetAppointment();
            setIsConfirmModalOpen(false);
            navigate(state?.returnPath);
        }
        if (!cancelChanges) setIsConfirmModalOpen(false);
    };

    const onDownload = () => {
        const document = documentDetailsSearch.find(
            (el) => el.attendanceId === state.appointmentAttendanceId
        );
        if (document) {
            dispatch(
                downloadParticipantDocumentById(
                    currentParticipant.id,
                    document.id,
                    document.filename
                )
            );
        }
    };

    // RENDER
    return (
        <div className={`${app.container} ${local.appointmentsContainer}`}>
            <div className={app.withSideBar}>
                <div className={local.headingContent}>
                    <h1 className={app.mainHeading}>
                        {state?.appointment?.type || ''}{' '}
                        {reverseFormatDate(state?.appointment?.date)}
                    </h1>
                </div>

                <Accordion
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'general'}
                    onChange={() => onAccordionChange('general')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header">
                        <AccordionHeader>
                            <div className={local.accordionWarning}>
                                {(initialAppointmentWarnings.gender ||
                                    initialAppointmentWarnings.dateOfBirth) && (
                                    <span>
                                        <WarningIcon />
                                    </span>
                                )}
                                General
                            </div>
                        </AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'general' && (
                            <General
                                onClose={onAccordionChange}
                                isDisabled={isDisabled}
                                isUniqueIdEditDisabled={isUniqueIdEditDisabled}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!showInitialAppointmentAccordion}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'initialAppointment'}
                    onChange={() => onAccordionChange('initialAppointment')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>
                            <div className={local.accordionWarning}>
                                {Object.values(initialAppointmentWarnings).length > 0 && (
                                    <span>
                                        <WarningIcon />
                                    </span>
                                )}
                                Initial Appointment
                            </div>
                        </AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            <InitialAppointment
                                onClose={onAccordionChange}
                                isDisabled={isDisabled}
                                updateInitialAppointment={onUpdateInitialAppointment}
                                isRequired={isRequired}
                                warnings={Object.values(initialAppointmentWarnings)}
                                savedAppointment={initialAppointment}
                            />
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'lastNotes'}
                    onChange={() => onAccordionChange('lastNotes')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Notes from last Appointment</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <NotesPast notes={currentAppointment.lastAppointmentNotes} />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.questionnaire}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'questionnaire'}
                    onChange={() => onAccordionChange('questionnaire')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>
                            <div className={local.accordionWarning}>
                                {initialAppointmentWarnings.diagnosticQuestionnaire && (
                                    <span>
                                        <WarningIcon />
                                    </span>
                                )}
                                Questionnaire
                            </div>
                        </AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'questionnaire' && (
                                <Questionnaire isViewOnly={isViewOnly} />
                            )}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.jobReadiness}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'jobGoals'}
                    onChange={() => onAccordionChange('jobGoals')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Job Readiness & Goals</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'jobGoals' && (
                                <JobGoals
                                    isViewOnly={isViewOnly}
                                    currentParticipant={currentParticipant}
                                />
                            )}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.barriers}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'barriers'}
                    onChange={() => onAccordionChange('barriers')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Barriers</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'barriers' && <Barriers isViewOnly={isViewOnly} />}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.actionPlan}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'actionPlan'}
                    onChange={() => onAccordionChange('actionPlan')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Action Plan</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'actionPlan' && <ActionPlan isViewOnly={isViewOnly} />}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.courses}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'courses'}
                    onChange={() => onAccordionChange('courses')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Courses</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection} id="courses">
                            {expanded === 'courses' && (
                                <AllActiveCourseAndProvisionManagement
                                    isViewOnly={isViewOnly}
                                    returnState={{
                                        ...state,
                                        returnUrl: 'appointments',
                                        panel: 'courses'
                                    }}
                                />
                            )}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.supportedJobSearch}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'supportedJobSearch'}
                    onChange={() => onAccordionChange('supportedJobSearch')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Supported Job Search</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'supportedJobSearch' && (
                                <SupportedJobSearchManagement isViewOnly={isViewOnly} />
                            )}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.request}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'requests'}
                    onChange={() => onAccordionChange('requests')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Requests</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'requests' && <Requests isViewOnly={isViewOnly} />}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    hidden={!isActive.vacancy}
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'vacancy'}
                    onChange={() => onAccordionChange('vacancy')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Vacancy</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className={local.componentSection}>
                            {expanded === 'vacancy' && <Vacancies isViewOnly={isViewOnly} />}
                        </div>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    slotProps={{ transition: { mountOnEnter: true } }}
                    expanded={expanded === 'notes'}
                    onChange={() => onAccordionChange('notes')}
                    disabled={isAccordionDisabled}>
                    <AccordionSummary
                        expandIcon={<AccordionIcon />}
                        aria-controls="panel2a-content"
                        id="panel2a-header">
                        <AccordionHeader>Appointment Notes</AccordionHeader>
                    </AccordionSummary>
                    <AccordionDetails>
                        <NotesCurrent
                            initialStateEntry={initialStateEntry}
                            notesCurrent={currentAppointment.currentNotes}
                            notesNext={currentAppointment.nextAppointmentNotes}
                            onUpdateNotes={onUpdateNotes}
                            isDisabled={isDisabled}
                            onClose={onAccordionChange}
                        />
                    </AccordionDetails>
                </Accordion>

                <div className={local.appointmentsToolbar}>
                    <div className={local.updateWrapper}>
                        <SingleSelect
                            id={'selectedAttendanceStatus'}
                            key={keys.selectedAttendanceStatus}
                            label="Attendance"
                            placeholder="Select an attendance status"
                            disabled={isDisabled}
                            customClass="widthControl"
                            menuItems={attendanceDetails || []}
                            selectedId={currentAppointment.attendanceStatusId}
                            selected={
                                attendanceDetails.find(
                                    (el) => el.id === currentAppointment.attendanceStatusId
                                ) || {}
                            }
                            onChange={(chosenId) => onAttendanceSelect(chosenId)}
                        />

                        {state.action === 'update' && (
                            <div className={local.updateButton}>
                                <Button
                                    id="updateAttendanceStatusButton"
                                    type="button"
                                    content="Update Attendance Status"
                                    disabled={
                                        errors?.updateAttendanceStatusButton?.error ||
                                        isUpdateDisabled
                                    }
                                    onClick={onUpdateAppointmentEntryAttendanceStatus}
                                    clearError={() => clearError('updateAttendanceStatusButton')}
                                />
                            </div>
                        )}
                    </div>

                    <MandatoryActivitiesSwitch
                        isMandatory={state?.appointment?.mandatory}
                        disabled={true}
                    />

                    <div className={local.completeFormActions}>
                        <OnFileDownloadIcon active={!isDownloadDisabled} onDownload={onDownload} />
                        <Button
                            id="appointmentsProceedButton"
                            disabled={isDisabled || isProceedButtonDisabled}
                            content="Proceed"
                            customErrorClass="formActionsError"
                            error={errors.appointmentsProceedButton}
                            clearError={() => clearError('appointmentsProceedButton')}
                            onClick={onProceed}
                        />
                        <CancelButton onClick={onCancelAppointment} />
                    </div>
                </div>

                {Object.values(errors).some((el) => el.error) && (
                    <div data-testid="appointment_screen_error">
                        {Object.entries(errors).map((el) =>
                            el[1].error ? (
                                <div key={el[0]}>
                                    <ScreenError
                                        error={el[1]}
                                        name={el[0]}
                                        clearError={clearError}
                                        customClass="relativePos"
                                        allowDismiss={false}
                                    />
                                </div>
                            ) : null
                        )}
                    </div>
                )}

                <div
                    className={`${local.warnings} ${app.fadein}`}
                    data-testid="appointment_screen_warning">
                    {Object.values(warnings).map((el) => (
                        <div key={el} className={local.warning}>
                            <WarningIcon />
                            {el}
                        </div>
                    ))}
                </div>

                {isSignatureModalOpen && (
                    <Dialog onClose={onSignatureModalClose} open={isSignatureModalOpen}>
                        <ESignature
                            currentParticipant={currentParticipant}
                            onParticipantSignatureSave={onParticipantSignatureSave}
                            onAdviserSignatureSave={onAdviserSignatureSave}
                            onSignatureSkip={onSignatureSkip}
                            onSignatureConfirm={onSignatureConfirm}
                            onSignatureCancel={onSignatureCancel}
                            attendanceName={attendanceName}
                        />
                    </Dialog>
                )}
                {generatePDF && (
                    <AppointmentPdfManagement
                        onSavePDF={onSavePDF}
                        fileName={fileName}
                        appointmentType={{
                            id: state?.appointment.typeId,
                            name: state?.appointment.type
                        }}
                        participantsActiveAccordions={participantsActiveAccordions}
                        adviserName={adviserName || 'User not logged in'}
                        currentParticipant={currentParticipant}
                        participantSignature={participantSignature}
                        adviserSignature={adviserSignature}
                        attendanceId={state?.appointmentAttendanceId}
                        currentAppointment={currentAppointment}
                        appointment={state?.appointment}
                        nextAppointment={nextAppointment}
                        signatureDateTime={signatureDateTime}
                        exitFormTemplateId={exitFormTemplateId}
                        doubtFormId={doubtFormId}
                        complianceActivityFormQuestionnaireId={
                            complianceActivityFormQuestionnaireId
                        }
                    />
                )}
                {isConfirmModalOpen && (
                    <ConfirmPrompt
                        isOpen={isConfirmModalOpen}
                        onCancel={(e) => onConfirmModalClose(e, false)}
                        onConfirm={(e) => onConfirmModalClose(e, true)}
                        content="Do you want to cancel these changes?"
                    />
                )}
                <div className={app.sideBar}>
                    <ParticipantStickyMenu />
                </div>
            </div>
        </div>
    );
};
export default Appointments;
